import * as React from 'react';

import {
  enumDocumentType,
  enumLoadStatus,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  Menu,
  MenuItem,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import {
  LucideDownload,
  LucideExternalLink,
  LucideEye,
  LucideMoreVertical,
  LucidePrinter,
  LucideTrash2,
  LucideUndo2,
} from 'lucide-react';

import { loadMarkAsVoidedAtom } from '../../_graphql/mutationLoadMarkAsVoided';
import { loadRevertStatusAtom } from '../../_graphql/mutationLoadRevertStatus';
import * as state from '../../_state/transactions.state';
import { usePrintWithBrowser } from '../../hooks/usePrintWithBrowser';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import { OutboundTransactionRecord } from '../../types/transaction';
import { canVoidLoad, getLoadRevertTargets } from '../../utils/status';

import { TransactionSidePanel } from './TransactionSidePanel';

type OutboundTransactionActionMenuProps = {
  transaction: OutboundTransactionRecord;
  onRefetch?: () => void;
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TRANSACTION ACTION MENU ⚛️ ⚛️ ⚛️
//
export const OutboundTransactionActionMenu = ({
  transaction,
  onRefetch,
}: OutboundTransactionActionMenuProps) => {
  const t = useTranslations();

  // State
  const [sidePanelOpen, setSidePanelOpen] = React.useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);

  // Atoms
  const setSelectedOutboundId = useSetAtom(
    state.$selectedOutboundTransactionId,
  );
  const voidMutation = useSetAtom(loadMarkAsVoidedAtom({}));
  const revertMutation = useSetAtom(loadRevertStatusAtom({}));

  // Data hooks
  const { print, isPrinting } = usePrintWithBrowser();
  const toast = useToast();

  // Callbacks
  const handlePrintInvoice = React.useCallback(() => {
    print({
      documents: [
        { documentType: enumDocumentType.Invoice, loadId: transaction.id },
      ],
    });
    toast.info(t.transactionsView.actions.printingInvoice);
  }, [print, transaction.id, t, toast]);

  const handlePrintPackingList = React.useCallback(() => {
    print({
      documents: [
        { documentType: enumDocumentType.PackingList, loadId: transaction.id },
      ],
    });
    toast.info(t.transactionsView.actions.printingPackingList);
  }, [print, transaction.id, t, toast]);

  const handlePrintBol = React.useCallback(() => {
    print({
      documents: [
        { documentType: enumDocumentType.BillOfLading, loadId: transaction.id },
      ],
    });
    toast.info(t.transactionsView.actions.printingBillOfLading);
  }, [print, transaction.id, t, toast]);

  const handleDownloadInvoice = React.useCallback(async () => {
    await print({
      documents: [
        { documentType: enumDocumentType.Invoice, loadId: transaction.id },
      ],
      action: 'download',
    });
  }, [print, transaction.id]);

  const handleDownloadPackingList = React.useCallback(async () => {
    await print({
      documents: [
        { documentType: enumDocumentType.PackingList, loadId: transaction.id },
      ],
      action: 'download',
    });
  }, [print, transaction.id]);

  const handlePrintWeightTicket = React.useCallback(() => {
    print({
      documents: [
        { documentType: enumDocumentType.WeightTicket, loadId: transaction.id },
      ],
    });
    toast.info(t.transactionsView.actions.printingWeightTicket);
  }, [print, transaction.id, t, toast]);

  const handlePrintOutboundScaleTicket = React.useCallback(() => {
    print({
      documents: [
        {
          documentType: enumDocumentType.OutboundScaleTicket,
          loadId: transaction.id,
        },
      ],
    });
    toast.info(t.transactionsView.actions.printingOutboundScaleTicket);
  }, [print, transaction.id, t, toast]);

  const handleDownloadBol = React.useCallback(async () => {
    await print({
      documents: [
        { documentType: enumDocumentType.BillOfLading, loadId: transaction.id },
      ],
      action: 'download',
    });
  }, [print, transaction.id]);

  const handleDownloadWeightTicket = React.useCallback(async () => {
    await print({
      documents: [
        { documentType: enumDocumentType.WeightTicket, loadId: transaction.id },
      ],
      action: 'download',
    });
  }, [print, transaction.id]);

  const handleDownloadOutboundScaleTicket = React.useCallback(async () => {
    await print({
      documents: [
        {
          documentType: enumDocumentType.OutboundScaleTicket,
          loadId: transaction.id,
        },
      ],
      action: 'download',
    });
  }, [print, transaction.id]);

  const handleOpenSidePanel = React.useCallback(() => {
    setSelectedOutboundId(transaction.id);
    setSidePanelOpen(true);
  }, [setSelectedOutboundId, transaction.id]);

  const handleViewSalesOrder = React.useCallback(() => {
    if (!transaction.salesOrderId) return;
    const url = tradeRoutePaths.salesOrderDetail.replace(
      ':id',
      transaction.salesOrderId,
    );
    window.open(url, '_blank');
  }, [transaction.salesOrderId]);

  const handleViewLoad = React.useCallback(() => {
    const url = tradeRoutePaths.loadDetail.replace(':id', transaction.id);
    window.open(url, '_blank');
  }, [transaction.id]);

  const handleVoidConfirm = React.useCallback(async () => {
    try {
      setIsProcessing(true);
      await voidMutation({ loadId: transaction.id });
      toast.success(t.transactionsView.actions.voidTicketSuccess);
      setVoidConfirmOpen(false);
      onRefetch?.();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.transactionsView.actions.voidTicketError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [voidMutation, transaction.id, toast, t, onRefetch]);

  const handleRevert = React.useCallback(
    async (targetStatus: LoadStatus) => {
      try {
        setIsProcessing(true);
        await revertMutation({ id: transaction.id, targetStatus });
        toast.success(t.transactionsView.actions.revertStatusSuccess);
        onRefetch?.();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : t.transactionsView.actions.revertStatusError,
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [revertMutation, transaction.id, toast, t, onRefetch],
  );

  // Memos
  const status = transaction.status as LoadStatus;

  const revertLabel: Record<string, string> = React.useMemo(
    () => ({
      [enumLoadStatus.Unassigned]: t.scaleTicketPro.statusUnassigned,
      [enumLoadStatus.Pending]: t.scaleTicketPro.statusPending,
      [enumLoadStatus.ContainerReadyToShip]:
        t.scaleTicketPro.statusContainerReadyToShip,
      [enumLoadStatus.Shipped]: t.scaleTicketPro.statusShipped,
      [enumLoadStatus.Received]: t.scaleTicketPro.statusReceived,
    }),
    [t],
  );

  const revertTargets = React.useMemo(
    () => getLoadRevertTargets(status),
    [status],
  );

  const showVoid = canVoidLoad(status);

  const menuItems = React.useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        label: t.transactionsView.actions.printDocuments,
        isDisabled: true,
        className: 'text-xs font-semibold uppercase text-neutral/50',
      },
      {
        label: t.transactionsView.actions.invoice,
        icon: <LucidePrinter />,
        onAction: handlePrintInvoice,
        isDisabled: isPrinting,
      },
      {
        label: t.transactionsView.actions.packingList,
        icon: <LucidePrinter />,
        onAction: handlePrintPackingList,
        isDisabled: isPrinting,
      },
      {
        label: t.transactionsView.actions.billOfLading,
        icon: <LucidePrinter />,
        onAction: handlePrintBol,
        isDisabled: isPrinting,
      },
      {
        label: t.transactionsView.actions.weightTicket,
        icon: <LucidePrinter />,
        onAction: handlePrintWeightTicket,
        isDisabled: isPrinting,
      },
      {
        label: t.transactionsView.actions.outboundScaleTicket,
        icon: <LucidePrinter />,
        onAction: handlePrintOutboundScaleTicket,
        isDisabled: isPrinting,
      },
      'separator',
      {
        label: t.transactionsView.actions.downloadDocuments,
        isDisabled: true,
        className: 'text-xs font-semibold uppercase text-neutral/50',
      },
      {
        label: t.transactionsView.actions.invoice,
        icon: <LucideDownload />,
        onAction: handleDownloadInvoice,
      },
      {
        label: t.transactionsView.actions.packingList,
        icon: <LucideDownload />,
        onAction: handleDownloadPackingList,
      },
      {
        label: t.transactionsView.actions.billOfLading,
        icon: <LucideDownload />,
        onAction: handleDownloadBol,
      },
      {
        label: t.transactionsView.actions.weightTicket,
        icon: <LucideDownload />,
        onAction: handleDownloadWeightTicket,
      },
      {
        label: t.transactionsView.actions.outboundScaleTicket,
        icon: <LucideDownload />,
        onAction: handleDownloadOutboundScaleTicket,
      },
      'separator',
      {
        label: t.transactionsView.actions.ticketDetails,
        isDisabled: true,
        className: 'text-xs font-semibold uppercase text-neutral/50',
      },
      {
        label: t.transactionsView.actions.viewAdditionalDetails,
        icon: <LucideEye />,
        onAction: handleOpenSidePanel,
      },
      {
        label: t.transactionsView.actions.viewLoad,
        icon: <LucideExternalLink />,
        onAction: handleViewLoad,
      },
      ...(transaction.salesOrderId
        ? [
            {
              label: t.transactionsView.actions.viewSalesOrder,
              icon: <LucideExternalLink />,
              onAction: handleViewSalesOrder,
            } satisfies MenuItem,
          ]
        : []),
      ...(revertTargets.length > 0 || showVoid ? ['separator' as const] : []),
      ...revertTargets.map(
        (targetStatus) =>
          ({
            label: t.transactionsView.actions.revertTo(
              revertLabel[targetStatus] ?? targetStatus,
            ),
            icon: <LucideUndo2 />,
            onAction: () => handleRevert(targetStatus),
            isDisabled: isProcessing,
          }) satisfies MenuItem,
      ),
      ...(showVoid
        ? [
            {
              label: t.transactionsView.actions.voidTicket,
              icon: <LucideTrash2 />,
              onAction: () => setVoidConfirmOpen(true),
              isDisabled: isProcessing,
            } satisfies MenuItem,
          ]
        : []),
    ];

    return items;
  }, [
    t,
    transaction.salesOrderId,
    revertTargets,
    revertLabel,
    showVoid,
    isProcessing,
    isPrinting,
    handlePrintInvoice,
    handlePrintPackingList,
    handlePrintBol,
    handlePrintWeightTicket,
    handlePrintOutboundScaleTicket,
    handleDownloadInvoice,
    handleDownloadPackingList,
    handleDownloadBol,
    handleDownloadWeightTicket,
    handleDownloadOutboundScaleTicket,
    handleOpenSidePanel,
    handleViewLoad,
    handleViewSalesOrder,
    handleRevert,
  ]);

  return (
    <div
      className="@OutboundTransactionActionMenu flex justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Menu
        trigger={
          <Button size="icon" icon={<LucideMoreVertical />} variant="outline" />
        }
        items={menuItems}
      />
      <TransactionSidePanel
        direction="outbound"
        documentLabel={transaction.loadNumber}
        isOpen={sidePanelOpen}
        onOpenChange={setSidePanelOpen}
      />
      {voidConfirmOpen && (
        <DialogOverlay
          isOpen
          isDismissable
          onOpenChange={(open) => !open && setVoidConfirmOpen(false)}
        >
          <DialogContent
            onOpenChange={(open: boolean) => !open && setVoidConfirmOpen(false)}
          >
            <DialogHeader>
              <span className="text-base font-semibold">
                {t.transactionsView.actions.voidTicketConfirmTitle}
              </span>
            </DialogHeader>
            <div className="flex flex-col gap-2 px-4">
              <p className="text-sm text-neutral/70">
                {t.scaleTicketPro.voidTicket.description}
              </p>
              <div className="flex justify-end gap-2 py-4">
                <Button
                  variant="text"
                  color="neutral"
                  onPress={() => setVoidConfirmOpen(false)}
                  isDisabled={isProcessing}
                >
                  {t.common.cancel}
                </Button>
                <Button
                  variant="solid"
                  color="failure"
                  icon={<LucideTrash2 className="size-4" />}
                  onPress={handleVoidConfirm}
                  isLoading={isProcessing}
                >
                  {t.scaleTicketPro.voidTicket.confirmButton}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}
    </div>
  );
};
